import {useEffect, useRef, useState} from 'react';

/**
 * Custom hook for Intersection Observer API
 * Detects when an element becomes visible in the viewport
 *
 * @param {Object} options - Intersection Observer options
 * @param {string} options.threshold - Percentage of visibility needed (0-1)
 * @param {string} options.rootMargin - Margin around root (e.g., '200px' to trigger 200px before visible)
 * @param {boolean} options.triggerOnce - Only trigger once (default: true)
 * @returns {Object} { ref, isVisible } - Ref to attach to element and visibility state
 */
export const useIntersectionObserver = ({
	                                        threshold = 0,
	                                        rootMargin = '200px',
	                                        triggerOnce = true,
                                        } = {}) => {
	const [isVisible, setIsVisible] = useState(false);
	const ref = useRef(null);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		// Check if browser supports Intersection Observer
		if (!('IntersectionObserver' in window)) {
			// Fallback: immediately set to visible
			setIsVisible(true);
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				const isElementVisible = entry.isIntersecting;

				if (isElementVisible) {
					setIsVisible(true);

					// If triggerOnce, disconnect observer after first trigger
					if (triggerOnce && observer) {
						observer.disconnect();
					}
				} else if (!triggerOnce) {
					setIsVisible(false);
				}
			},
			{
				threshold,
				rootMargin,
			}
		);

		observer.observe(element);

		// Cleanup
		return () => {
			if (observer && element) {
				observer.unobserve(element);
			}
		};
	}, [threshold, rootMargin, triggerOnce]);

	return {ref, isVisible};
};
